import { useEffect, useState } from 'react';
import {
  isFeatureEnabled,
  isFeatureEnabledNow,
  onFeatureFlagsChange,
} from './analytics';
import type { FeatureFlagName } from './featureFlags';

/**
 * A Mixpanel feature flag's value for the current user, checked again when
 * the user is identified. Starts at `fallback` until flags have been fetched
 * (unless they already were), and stays there
 * when analytics is off, so the fallback is what tests and token-less
 * environments see.
 */
export const useFeatureFlag = (
  flag: FeatureFlagName,
  fallback = false
): boolean => {
  const [enabled, setEnabled] = useState(() =>
    isFeatureEnabledNow(flag, fallback)
  );

  useEffect(() => {
    // Only the latest check may set state: an older one can still be waiting
    // on the anonymous user's flags.
    let latest = 0;
    const check = () => {
      const call = ++latest;
      isFeatureEnabled(flag, fallback).then((value) => {
        if (call === latest) setEnabled(value);
      });
    };

    check();
    const unsubscribe = onFeatureFlagsChange(check);
    return () => {
      latest = -1;
      unsubscribe();
    };
  }, [flag, fallback]);

  return enabled;
};
